import type { GsapTimelineLike } from '../editableVisibility';
import type { LintFinding, LintSeverity } from './types';

/**
 * The loaded composition, for rules that need more than the HTML source — the live DOM and the
 * seekable timeline. `timeline` is null when the composition declares no `window.__timelines.main`.
 */
export interface RuntimeLintContext {
    document: Document;
    timeline: GsapTimelineLike | null;
}

/**
 * The runtime counterpart to the static `LintRule`: it inspects the loaded composition rather than
 * the raw HTML string. The asset runs these after loading the iframe and merges their findings with
 * the static ones into one `LintFinding[]`.
 */
export interface RuntimeLintRule {
    id: string;
    severity: LintSeverity;
    guidance?: string;
    check(context: RuntimeLintContext): string[];
}

const TEXT_NODE = 3;
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
// Elements whose text content is never rendered as on-screen copy, so it needs no editable decision.
const NON_RENDERED_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'TITLE']);
// Inline-level tags, used only as a fallback when the computed display is unavailable (jsdom returns
// an empty display for inline elements). In a real browser getComputedStyle is authoritative.
const INLINE_TAGS = new Set([
    'A',
    'ABBR',
    'B',
    'BDI',
    'BDO',
    'CITE',
    'CODE',
    'DATA',
    'DEL',
    'DFN',
    'EM',
    'I',
    'INS',
    'KBD',
    'LABEL',
    'MARK',
    'Q',
    'S',
    'SAMP',
    'SMALL',
    'SPAN',
    'STRONG',
    'SUB',
    'SUP',
    'TIME',
    'U',
    'VAR',
]);

// True when the element renders on-screen copy directly: it has non-whitespace text and its only
// element children are <br> line breaks (which editable text may legitimately contain). A wrapper
// with other element children is structural, not a text leaf, and is inspected via its descendants.
function isTextLeaf(element: Element): boolean {
    const onlyLineBreakChildren = Array.from(element.children).every(
        (child) => child.tagName === 'BR',
    );
    if (!onlyLineBreakChildren) {
        return false;
    }
    return (element.textContent ?? '').trim().length > 0;
}

// True when the element holds its own non-whitespace text directly (mixed inline content).
function holdsOwnText(element: Element): boolean {
    return Array.from(element.childNodes).some(
        (node) => node.nodeType === TEXT_NODE && (node.textContent ?? '').trim().length > 0,
    );
}

// True when the element is inline-level — a piece of flowing text rather than a standalone block.
// Prefer the computed display (authoritative, honours CSS overrides); fall back to the tag name when
// display is unavailable. Only inline leaves are exempted as sentence-pieces of a mixed-content
// parent; a block-level leaf (e.g. a <p> beside loose text) is its own text leaf and must be labelled.
function isInlineLevel(element: Element): boolean {
    const display = element.ownerDocument.defaultView?.getComputedStyle(element).display ?? '';
    if (display) {
        return display.startsWith('inline') || display === 'contents';
    }
    return INLINE_TAGS.has(element.tagName);
}

/**
 * Every on-screen text leaf must declare whether the user can edit it in place, via an explicit
 * `data-l5-editable="true"` or `data-l5-editable="false"`. This makes an omitted decision impossible
 * to ship silently: an unlabeled (or bare, or invalidly-valued) text node is an error the agent must
 * resolve. SVG text and mixed inline content are out of scope for v1.
 */
export const textRequiresEditableLabel: RuntimeLintRule = {
    id: 'text-requires-editable-label',
    severity: 'error',
    guidance:
        'Give EVERY on-screen text element an explicit `data-l5-editable="true"` (user can edit the copy in place) or `data-l5-editable="false"` (locked — e.g. a counter, unit, or value-driven number). There is no default and no bare form: an unlabeled text node is an error.',
    check(context: RuntimeLintContext): string[] {
        const body = context.document.body;
        if (!body) {
            return [];
        }
        const messages: string[] = [];
        for (const element of Array.from(body.querySelectorAll('*'))) {
            if (NON_RENDERED_TAGS.has(element.tagName) || element.tagName === 'BR') {
                continue;
            }
            if (element.namespaceURI === SVG_NAMESPACE) {
                continue;
            }
            if (!isTextLeaf(element)) {
                continue;
            }
            // An inline leaf sitting among a parent's own text (e.g. <strong> in a sentence) is a
            // piece of that text, not a standalone leaf, so it needs no decision of its own. A
            // block-level leaf is standalone even when a sibling text node is present.
            const parent = element.parentElement;
            if (parent && holdsOwnText(parent) && isInlineLevel(element)) {
                continue;
            }
            const value = element.getAttribute('data-l5-editable');
            if (value === 'true' || value === 'false') {
                continue;
            }
            const idPart = element.id ? ` (#${element.id})` : '';
            messages.push(
                `Text element <${element.tagName.toLowerCase()}>${idPart} is missing an explicit ` +
                    '`data-l5-editable="true"` or `data-l5-editable="false"`. Every on-screen text ' +
                    'node must declare whether the user can edit it in place.',
            );
        }
        return messages;
    },
};

export const CORE_RUNTIME_LINT_RULES: RuntimeLintRule[] = [textRequiresEditableLabel];

/**
 * Run the runtime rules against a loaded composition. Synchronous (the composition is already
 * loaded); the asset awaits the load, then calls this.
 */
export function runRuntimeLintRules(
    context: RuntimeLintContext,
    rules: RuntimeLintRule[] = CORE_RUNTIME_LINT_RULES,
): LintFinding[] {
    const findings: LintFinding[] = [];
    for (const rule of rules) {
        for (const message of rule.check(context)) {
            findings.push({
                ruleId: rule.id,
                severity: rule.severity,
                message,
            });
        }
    }
    return findings;
}
